import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Lets a full-screen page (the website editor) take over the dashboard shell.
 *
 * Covering the sidebar with an opaque overlay is not enough: the nav stays in the
 * tab order and in the accessibility tree, so keyboard and screen-reader users
 * land on links they cannot see. Hiding the chrome outright is the honest fix.
 */
interface DashboardChromeValue {
  chromeHidden: boolean;
  requestHiddenChrome: () => () => void;
}

const noop = () => () => {};

const DashboardChromeContext = createContext<DashboardChromeValue>({
  chromeHidden: false,
  requestHiddenChrome: noop,
});

export const DashboardChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Ref-counted: nested/overlapping consumers must not un-hide the chrome early.
  const [holders, setHolders] = useState(0);

  const requestHiddenChrome = useCallback(() => {
    setHolders((count) => count + 1);
    return () => setHolders((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({ chromeHidden: holders > 0, requestHiddenChrome }),
    [holders, requestHiddenChrome],
  );

  return (
    <DashboardChromeContext.Provider value={value}>
      {children}
    </DashboardChromeContext.Provider>
  );
};

export function useDashboardChrome(): DashboardChromeValue {
  return useContext(DashboardChromeContext);
}

/** Hides the dashboard sidebar and mobile top bar for as long as the caller is mounted. */
export function useHideDashboardChrome(): void {
  const { requestHiddenChrome } = useDashboardChrome();
  useEffect(() => requestHiddenChrome(), [requestHiddenChrome]);
}
